/**
 * Anonymous (pre-login) identity + first-touch attribution (Growth Intelligence Engine — Phase 0.5).
 *
 * Top-of-funnel visitors have no authenticated correlation id yet, so we mint an opaque
 * client-random `anonCid` on first visit and capture first-touch attribution ONCE. The sink
 * (sink.ts) routes behavioral events to the PUBLIC `/telemetry/anon-events` endpoint, tagged with
 * this anonCid + attribution, until a user cid arrives; the anonCid then rides the signup request
 * and an `identity-stitched` event so the backend can link anon → user.
 *
 * Design rules (identical posture to sink.ts / clarity.ts):
 *  - NEVER throws, NEVER blocks the UI. Every function swallows its own errors and degrades to a
 *    safe empty value when storage / the DOM is unavailable.
 *  - NO PII: the anonCid is opaque client-random; attribution values are scrubbed with the same
 *    `sanitizeTagValue` used everywhere else.
 */

import { sanitizeTagValue } from './clarity';

const ANON_CID_KEY = 'bizcore_anon_cid';
const ANON_ATTR_KEY = 'bizcore_anon_attr';

/** First-touch attribution — captured ONCE on first visit, never overwritten. No PII. */
export interface AnonAttribution {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  referrer?: string;
  landingPath?: string;
}

/** Opaque client-random id. Prefers crypto.randomUUID; falls back to a base36 random. */
function randomId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through to the base36 fallback */
  }
  return `a-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * The anonymous correlation id for this browser. Generated + persisted on first call if absent.
 * Returns '' when localStorage is unavailable (never throws).
 */
export function getAnonCid(): string {
  try {
    if (typeof window === 'undefined') return '';
    let cid = window.localStorage.getItem(ANON_CID_KEY);
    if (!cid) {
      cid = randomId();
      window.localStorage.setItem(ANON_CID_KEY, cid);
    }
    return cid;
  } catch {
    return '';
  }
}

/**
 * Capture first-touch attribution exactly once (persisted). Idempotent + never overwrites an
 * existing capture, so the FIRST landing's source/medium/campaign + referrer + path win. No-op on
 * any error.
 */
export function captureFirstTouchAttribution(): void {
  try {
    if (typeof window === 'undefined') return;
    if (window.localStorage.getItem(ANON_ATTR_KEY)) return; // first-touch only — never overwrite

    const params = new URLSearchParams(window.location.search);
    const attr: AnonAttribution = {};
    const utmSource = params.get('utm_source');
    const utmMedium = params.get('utm_medium');
    const utmCampaign = params.get('utm_campaign');
    if (utmSource) attr.utmSource = sanitizeTagValue(utmSource);
    if (utmMedium) attr.utmMedium = sanitizeTagValue(utmMedium);
    if (utmCampaign) attr.utmCampaign = sanitizeTagValue(utmCampaign);
    const referrer = typeof document !== 'undefined' ? document.referrer : '';
    if (referrer) attr.referrer = sanitizeTagValue(referrer);
    attr.landingPath = sanitizeTagValue(window.location.pathname);

    window.localStorage.setItem(ANON_ATTR_KEY, JSON.stringify(attr));
  } catch {
    /* attribution capture is best-effort; never throw */
  }
}

/** The persisted first-touch attribution, or undefined if none captured / on any error. */
export function getAttribution(): AnonAttribution | undefined {
  try {
    if (typeof window === 'undefined') return undefined;
    const raw = window.localStorage.getItem(ANON_ATTR_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as AnonAttribution;
    return parsed && typeof parsed === 'object' ? parsed : undefined;
  } catch {
    return undefined;
  }
}
